// ─────────────────────────────────────────────────────────────────────────────
// agent_memory.ts — Conversation persistence + notification queues
//
// Uses IndexedDB for durable storage.  Each persona gets an independent
// conversation thread.  Messages compress via rolling summaries when they
// exceed the token budget.  Notification queues accumulate while offline
// and drain on the next interaction or app open.
//
// Storage layout (IndexedDB "grimoire-agent"):
//   threads/{personaId}     → ConversationThread
//   notifications/{id}      → NotificationEntry
//   fold-state              → FoldState (singleton)
// ─────────────────────────────────────────────────────────────────────────────

import type {
  PersonaId, ConversationThread, ChatMessage,
  NotificationEntry, NotificationTrigger,
} from '@shared/agent_protocol.js';
import type { FoldState } from '@shared/the_fold.js';
import { defaultFoldState } from '@shared/the_fold.js';
import { estimateTokens } from './llm_client.js';

// ── IndexedDB wrapper ────────────────────────────────────────────────────────

const DB_NAME = 'grimoire-agent';
const DB_VERSION = 1;

const STORE_THREADS = 'threads';
const STORE_NOTIFICATIONS = 'notifications';
const STORE_FOLD = 'fold';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_THREADS)) {
        db.createObjectStore(STORE_THREADS, { keyPath: 'persona' });
      }
      if (!db.objectStoreNames.contains(STORE_NOTIFICATIONS)) {
        const store = db.createObjectStore(STORE_NOTIFICATIONS, { keyPath: 'id' });
        store.createIndex('persona', 'persona', { unique: false });
        store.createIndex('processed', 'processed', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_FOLD)) {
        db.createObjectStore(STORE_FOLD, { keyPath: 'name' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet<T>(db: IDBDatabase, store: string, key: IDBValidKey): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, store: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbGetAll<T>(db: IDBDatabase, store: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(db: IDBDatabase, store: string, key: IDBValidKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Agent memory manager ─────────────────────────────────────────────────────

export class AgentMemory {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    this.db = await openDb();
  }

  private ensureDb(): IDBDatabase {
    if (!this.db) throw new Error('AgentMemory not initialised — call init() first');
    return this.db;
  }

  // ── Conversation threads ─────────────────────────────────────────────────

  async getThread(persona: PersonaId): Promise<ConversationThread> {
    const db = this.ensureDb();
    const existing = await idbGet<ConversationThread>(db, STORE_THREADS, persona);
    if (existing) return existing;
    return {
      persona,
      messages: [],
      lastActive: 0,
      totalTokensEstimate: 0,
    };
  }

  async appendMessage(persona: PersonaId, message: ChatMessage): Promise<void> {
    const thread = await this.getThread(persona);
    const tokenEst = message.tokenEstimate ?? estimateTokens(message.content);
    thread.messages.push({ ...message, tokenEstimate: tokenEst });
    thread.totalTokensEstimate += tokenEst + 4;
    thread.lastActive = Date.now();
    await idbPut(this.ensureDb(), STORE_THREADS, thread);
  }

  async getRecentMessages(persona: PersonaId, maxTokens: number): Promise<ChatMessage[]> {
    const thread = await this.getThread(persona);
    const msgs = thread.messages;
    const result: ChatMessage[] = [];
    let tokens = 0;

    // Walk backwards, keeping most recent messages
    for (let i = msgs.length - 1; i >= 0; i--) {
      const cost = (msgs[i].tokenEstimate ?? estimateTokens(msgs[i].content)) + 4;
      if (tokens + cost > maxTokens) break;
      tokens += cost;
      result.unshift(msgs[i]);
    }

    // Prepend summary if available and we have budget
    if (thread.summary) {
      const sumCost = estimateTokens(thread.summary) + 4;
      if (tokens + sumCost <= maxTokens) {
        result.unshift({
          role: 'system',
          content: `[Previous conversation summary]\n${thread.summary}`,
          timestamp: 0,
          tokenEstimate: sumCost,
        });
      }
    }

    return result;
  }

  /**
   * Compress older messages into a rolling summary.
   * Call this periodically when the thread gets too long.
   * The summary text is provided externally (by an LLM call).
   */
  async compressThread(persona: PersonaId, summary: string, keepRecentCount: number): Promise<void> {
    const thread = await this.getThread(persona);
    const kept = thread.messages.slice(-keepRecentCount);
    thread.summary = summary;
    thread.messages = kept;
    thread.totalTokensEstimate = kept.reduce(
      (sum, m) => sum + (m.tokenEstimate ?? estimateTokens(m.content)) + 4,
      estimateTokens(summary) + 4,
    );
    await idbPut(this.ensureDb(), STORE_THREADS, thread);
  }

  // ── Notification queue ───────────────────────────────────────────────────

  async pushNotification(
    persona: PersonaId,
    trigger: NotificationTrigger,
    payload: Record<string, unknown> = {},
  ): Promise<void> {
    const entry: NotificationEntry = {
      id: `notif_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      persona,
      trigger,
      payload,
      timestamp: Date.now(),
      processed: false,
    };
    await idbPut(this.ensureDb(), STORE_NOTIFICATIONS, entry);
  }

  async getPendingNotifications(persona: PersonaId): Promise<NotificationEntry[]> {
    const all = await idbGetAll<NotificationEntry>(this.ensureDb(), STORE_NOTIFICATIONS);
    return all
      .filter(n => n.persona === persona && !n.processed)
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  async markNotificationProcessed(id: string): Promise<void> {
    const db = this.ensureDb();
    const entry = await idbGet<NotificationEntry>(db, STORE_NOTIFICATIONS, id);
    if (entry) {
      entry.processed = true;
      await idbPut(db, STORE_NOTIFICATIONS, entry);
    }
  }

  async clearProcessedNotifications(): Promise<void> {
    const all = await idbGetAll<NotificationEntry>(this.ensureDb(), STORE_NOTIFICATIONS);
    const db = this.ensureDb();
    for (const n of all) {
      if (n.processed) {
        await idbDelete(db, STORE_NOTIFICATIONS, n.id);
      }
    }
  }

  // ── Fold state ───────────────────────────────────────────────────────────

  async getFoldState(): Promise<FoldState> {
    const db = this.ensureDb();
    const existing = await idbGet<FoldState>(db, STORE_FOLD, 'The Fold');
    return existing ?? defaultFoldState();
  }

  async saveFoldState(state: FoldState): Promise<void> {
    await idbPut(this.ensureDb(), STORE_FOLD, state);
  }

  async updateFoldState(updater: (state: FoldState) => void): Promise<FoldState> {
    const state = await this.getFoldState();
    updater(state);
    await this.saveFoldState(state);
    return state;
  }
}
